import React, { useEffect, useMemo, useState } from 'react';
import { Form, FormBuilder } from '@formio/react';
import { Button, Tooltip, TextField, Dialog, DialogTitle, DialogActions, DialogContent, FormGroup, FormControlLabel, Checkbox, IconButton, Autocomplete } from '@mui/material';
import Snackbar from '@mui/material/Snackbar';
import MuiAlert from '@mui/material/Alert';
import CloseIcon from '@mui/icons-material/Close';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import SaveIcon from '@mui/icons-material/Save';
import HighlightOffIcon from '@mui/icons-material/HighlightOff';
import axios from 'axios'
import { APIURL } from '../../../config';
import '../builderSidebarSticky.css';

const formEntrySx = { py: 0.25 };

export default function FormLibrary(props) {
    const [formSchema, setFormSchema] = useState('');
    const [formList, setFormList] = useState([]);
    const [formNamesList, setFormNamesList] = useState([]);
    const [subscribedForms, setSubscribedForms] = useState([]);
    const [publicForms, setPublicForms] = useState([]);
    const [formID, setFormID] = useState('');
    const [formName, setFormName] = useState('');
    const [formDesc, setFormDesc] = useState('');
    const [formType, setFormType] = useState('');
    const [customerFormCount, setCustomerFormCount] = useState(0);
    const [loading, setLoading] = useState(true);
    const [isEdit, setIsEdit] = useState(false);
    const [isEditable, setIsEditable] = useState(false);
    const [errorAlert, setErrorAlert] = useState('');
    const [successAlert, setSuccessAlert] = useState('');
    const [open, setOpen] = useState(false);
    const [maxFormID, setMaxFormID] = useState(0);
    const [isPublicForm, setIsPublicForm] = useState(0);
    const [selectedLanguage, setSelectedLanguage] = useState(null);
    const [translatedFormSchema, setTranslatedFormSchema] = useState('');
    const [formCopyTitle, setFormCopyTitle] = useState('');
    const [formCopyDesc, setFormCopyDesc] = useState('');
    const [formCopyType, setFormCopyType] = useState('');
    const [openEditDialog, setOpenEditDialog] = useState(false);
    const [openDeleteDialog, setOpenDeleteDialog] = useState(false);

    const languages = [
        { value: 'es', label: 'Spanish' },
    ];

    useEffect(() => {
        const fetchFormsData = async () => {
            try {
                const maxFormIDRes = await axios.get(`${APIURL}/task_form/formBuilder`);
                const formsRes = await axios.get(`${APIURL}/task_form/forms`);
                const subscribedFormsRes = await axios.get(`${APIURL}/task_form/subscribed?company_id=${props.comp_id}`);
                const allForms = formsRes.data;
                const subscribed_forms = subscribedFormsRes.data;
                let formio_forms = allForms.filter((form) => form.form_company === props.comp_id && !form.public_form && form.form_status !== 2);
                formio_forms = formio_forms.filter((form) => !subscribed_forms.some((sub) => sub.form_id === form.form_id));
                const public_forms = allForms.filter((form) => form.public_form);
                setMaxFormID(maxFormIDRes.data[0][""]);
                setFormNamesList(allForms.map((form) => form.form_name));
                setFormList(formio_forms);
                setSubscribedForms(subscribed_forms);
                setPublicForms(public_forms);
                setLoading(false);
            } catch (error) {
                console.error('Error fetching form library data:', error);
            }
        };
        fetchFormsData();
    }, [open, openDeleteDialog]);

    useEffect(() => {
        const customerFormCount = async () => {
            const formCount = await axios.get(`${APIURL}/customer_form/formCount/${formID}`);
            setCustomerFormCount(formCount.data[0].count);
        };

        if (formID) {
            customerFormCount();
        }
    }, [formID]);

    const handleFormSelect = (form) => {
        setFormSchema(form.json_schema);
        setTranslatedFormSchema(form?.translated_json_schema ? form?.translated_json_schema : form.json_schema)
        setIsEdit(false);
        setFormID(form.form_id);
        setFormName(form.form_name);
        setFormDesc(form.form_description);
        setFormType(form.form_type);
        setSelectedLanguage(form.form_language ? languages.find(lang => lang.value === form.form_language) : null);
    }

    const handleFormSchemaChange = (form, schema) => {
        const current = schema || form;
        current.display = 'form';
        setFormSchema(JSON.stringify(current));
    }

    const builderInitialForm = useMemo(() => {
        if (!isEdit || !formSchema) return { display: 'form', components: [] };
        try {
            return JSON.parse(formSchema);
        } catch (e) {
            return { display: 'form', components: [] };
        }
        // Intentionally omit formSchema from deps: the builder manages its own
        // state after mount; re-passing form on each edit causes setForm loops
        // that overwrite in-progress changes with stale data.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [formID, isEdit]);

    const editForm = async () => {
        await axios.put(APIURL + `/task_form/formBuilder`, {
            form_id: formID,
            form_name: formName,
            json_schema: JSON.parse(formSchema),
            public_form: isPublicForm,
            form_language: selectedLanguage ? selectedLanguage.value : null
        })
            .then(function (response) {
                setSuccessAlert('Form successfully edited. Please reload!')
            })
            .catch(function (error) {
                console.log(error);
                setErrorAlert('An error occured while trying to edit the form')
            });

        setIsEdit(false);
        handleClose();
    };

    const addFormCopy = async () => {
        if (formNamesList.includes(formCopyTitle)) {
            alert(`Form Title "${formCopyTitle}" already exists. Please choose a different title.`);
            return;
        }

        if (formCopyTitle.replace(/\s/g, '').length !== 0 && formCopyType.replace(/\s/g, '').length !== 0) {
            await axios.post(APIURL + `/task_form/formBuilder`, {
                form_id: maxFormID + 1,
                form_name: formCopyTitle,
                form_description: formCopyDesc,
                form_added_by: props.user.emp_id,
                form_type: formCopyType,
                json_schema: JSON.parse(formSchema),
                form_company: props.comp_id,
                public_form: isPublicForm,
                form_language: selectedLanguage ? selectedLanguage.value : null
            })
                .then(function (response) {
                    setSuccessAlert('Form successfully added')
                })
                .catch(function (error) {
                    console.log(error);
                    setErrorAlert('An error occured while trying to add the form')
                });

            handleClose();
        } else {
            if (!formCopyTitle) {
                alert(`Form Title is required to add the form`)
            }
            else if (!formCopyType) {
                alert(`Form Type is required to add the form`)
            } else {
                alert(`Please enter the required fields to add the form`)
            }
        }
    };

    const handleCopyButtonClick = async () => {
        setOpen(true);
        setFormCopyTitle(`Copy 0 of the ${formName}`);
        setFormCopyDesc(formDesc);
        setFormCopyType(formType);
    };

    const deleteForm = async () => {
        await axios.delete(APIURL + `/task_form/formBuilder`, {
            data: {
                form_id: formID,
            }
        })
            .then(function (response) {
                setSuccessAlert('Form successfully deleted. Please reload!')
            })
            .catch(function (error) {
                console.log(error);
                setErrorAlert('An error occured while trying to delete the form')
            });

        setIsEdit(false);
        handleClose();
    };

    const handleDeteleButtonClick = async () => {
        setOpenDeleteDialog(true);
    };

    const handleErrorAlertClose = (event, reason) => {
        if (reason === 'clickaway') {
            return;
        }
        setErrorAlert('');
    }

    const handleSuccessAlertClose = (event, reason) => {
        if (reason === 'clickaway') {
            return;
        }
        setSuccessAlert('');
    }

    const handleClose = () => {
        setOpen(false);
        setOpenDeleteDialog(false);
        setIsPublicForm(0);
    };

    return (
        <div>
            <div style={{ maxHeight: '33vh' }}>
                {!!loading && (
                    <div className="d-flex flex-row w-100 justify-content-center mt-5">
                        <div className="spinner-border text-primary" role="status">
                            <span className="sr-only">Loading...</span>
                        </div>
                    </div>
                )}
                {!loading &&
                    <div style={{ display: 'flex', justifyContent: 'flex-start' }}>
                        <div style={{ width: '33%' }}>
                            <h5 style={{ marginBottom: 10 }}>Your Forms</h5>
                            <div style={{ maxHeight: '30vh', overflowY: 'auto' }}>
                                {formList.map((form) => (
                                    <div key={form.form_id}>
                                        <Button sx={formEntrySx} onClick={() => { handleFormSelect(form); setIsEditable(true); }}>
                                            {form.form_name}
                                        </Button>
                                    </div>
                                ))}
                            </div>
                        </div>
                        <div style={{ width: '33%', borderLeft: '1px solid #ccc', paddingLeft: '10px' }}>
                            <h5 style={{ marginBottom: 10 }}>Community Forms</h5>
                            <div style={{ maxHeight: '30vh', overflowY: 'auto' }}>
                                {publicForms.map((form) => (
                                    <div key={form.form_id}>
                                        <Button sx={formEntrySx} onClick={() => { handleFormSelect(form); setIsEditable(false); }}>
                                            {form.form_name}
                                        </Button>
                                    </div>
                                ))}
                            </div>
                        </div>
                        <div style={{ width: '33%', borderLeft: '1px solid #ccc', paddingLeft: '10px' }}>
                            <h5 style={{ marginBottom: 10 }}>Subscribed Forms</h5>
                            <div style={{ maxHeight: '30vh', overflowY: 'auto' }}>
                                {subscribedForms.map((form) => (
                                    <div key={form.form_id}>
                                        <Button sx={formEntrySx} onClick={() => { handleFormSelect(form); setIsEditable(false); }}>
                                            {form.form_name}
                                        </Button>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                }
            </div>
            <hr style={{ borderTop: '1px solid #ccc' }} />
            {formSchema && !isEdit &&
                <div>
                    <h4 style={{ textDecoration: 'underline' }}>{formName}</h4>
                    <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: -45 }}>
                        {isEditable &&
                            <Tooltip title="Edit Form">
                                <IconButton onClick={() => customerFormCount > 0 ? setOpenEditDialog(true) : setIsEdit(true)} color='primary'>
                                    <EditIcon />
                                </IconButton>
                            </Tooltip>
                        }

                        <Tooltip title="Copy Form">
                            <IconButton onClick={handleCopyButtonClick} color='secondary'>
                                <ContentCopyIcon />
                            </IconButton>
                        </Tooltip>

                        {isEditable &&
                            <Tooltip title="Delete Form">
                                <IconButton onClick={handleDeteleButtonClick} color='error'>
                                    <DeleteIcon />
                                </IconButton>
                            </Tooltip>
                        }
                    </div>
                    <div style={{ border: '1px solid #ccc', padding: 10, marginTop: isEditable ? 10 : 20 }}>
                        <Form form={JSON.parse(translatedFormSchema)} options={{ readOnly: true }} onSubmit={() => setErrorAlert('This is only a test. For submitting an actual form, please follow the appropriate steps.')} />
                    </div>
                </div>
            }
            {isEdit &&
                <div>
                    <div style={{ display: 'flex' }}>
                        <TextField
                            size="small"
                            label="Form Title *"
                            value={formName}
                            onChange={(event) => setFormName(event.target.value)}
                            sx={{ marginRight: '10px', width: '450px' }}
                            inputProps={{ maxLength: 50 }}
                            error={formName ? false : true}
                        />
                        <Autocomplete
                            id="language-select"
                            options={languages}
                            value={selectedLanguage}
                            getOptionLabel={(option) => option.label}
                            isOptionEqualToValue={(option, value) => option.value === value.value}
                            onChange={(event, value) => { setSelectedLanguage(value); }}
                            renderOption={(props, option) => (
                                <li {...props} key={option.value}>
                                    {option.label}
                                </li>
                            )}
                            renderInput={(params) => <TextField {...params} size="small" label="Select Language" sx={{ marginRight: '10px', width: '250px' }} />}
                        />
                        <FormGroup>
                            <FormControlLabel
                                control={<Checkbox
                                    checked={isPublicForm}
                                    onChange={(event) => setIsPublicForm(event.target.checked ? 1 : 0)}
                                />}
                                label="Community Form"
                            />
                        </FormGroup>
                    </div>

                    <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: -45 }}>
                        <Tooltip title="Save Form">
                            <IconButton onClick={() => editForm()} color='primary'>
                                <SaveIcon />
                            </IconButton>
                        </Tooltip>
                        <Tooltip title="Cancel Edit">
                            <IconButton onClick={() => setIsEdit(false)} color='error'>
                                <HighlightOffIcon />
                            </IconButton>
                        </Tooltip>
                    </div>
                    <div className="formbuilder-scroll-wrap" style={{ border: '1px solid #ccc', padding: 10 }}>
                        <FormBuilder form={builderInitialForm} options={{ builder: { premium: false } }} onChange={(form, schema) => handleFormSchemaChange(form, schema)} />
                    </div>
                </div>
            }
            <Snackbar open={successAlert != ''} autoHideDuration={2000} onClose={handleSuccessAlertClose}>
                <MuiAlert sx={{ width: '100%' }} severity={"success"} elevation={6} variant="filled" {...props}>
                    {successAlert}
                    <IconButton
                        aria-label="close"
                        color="inherit"
                        sx={{ p: 0.25 }}
                        onClick={handleSuccessAlertClose}
                    >
                        <CloseIcon />
                    </IconButton>
                </MuiAlert>
            </Snackbar>
            <Snackbar open={errorAlert != ''} autoHideDuration={2000} onClose={handleErrorAlertClose}>
                <MuiAlert sx={{ width: '100%' }} severity={"error"} elevation={6} variant="filled" {...props}>
                    {errorAlert}
                    <IconButton
                        aria-label="close"
                        color="inherit"
                        sx={{ p: 0.25 }}
                        onClick={handleErrorAlertClose}
                    >
                        <CloseIcon />
                    </IconButton>
                </MuiAlert>
            </Snackbar>

            <Dialog fullWidth={true} maxWidth={'sm'} open={open} onClose={handleClose}>
                <DialogTitle sx={{ width: '100%', textAlign: 'center', borderBottom: '1px solid lightgray' }}>
                    Copy Form
                </DialogTitle>
                <DialogContent sx={{ paddingTop: '0px' }}>
                    <TextField
                        label="Form Title *"
                        value={formCopyTitle}
                        sx={{ marginTop: 2, marginBottom: 2 }}
                        onChange={(event) => setFormCopyTitle(event.target.value)}
                        fullWidth
                        error={formNamesList.includes(formCopyTitle) || !formCopyTitle}
                        helperText={formNamesList.includes(formCopyTitle) ? "Form Title already exists" : ""}
                    />
                    <TextField
                        label="Form Description"
                        value={formCopyDesc}
                        sx={{ marginTop: 2, marginBottom: 2 }}
                        onChange={(event) => setFormCopyDesc(event.target.value)}
                        fullWidth
                    />
                    <TextField
                        label="Form Type *"
                        value={formCopyType}
                        sx={{ marginTop: 2, marginBottom: 2 }}
                        onChange={(event) => setFormCopyType(event.target.value)}
                        fullWidth
                        error={!formCopyType}
                    />
                    <Autocomplete
                        id="language-select"
                        options={languages}
                        value={selectedLanguage}
                        getOptionLabel={(option) => option.label}
                        isOptionEqualToValue={(option, value) => option.value === value.value}
                        onChange={(event, value) => { setSelectedLanguage(value); }}
                        renderOption={(props, option) => (
                            <li {...props} key={option.value}>
                                {option.label}
                            </li>
                        )}
                        renderInput={(params) => <TextField {...params} label="Select Language" helperText="Language the form will be translated to. Default: English" />}
                        sx={{ marginTop: 2, marginBottom: 2 }}
                    />
                    <FormGroup>
                        <FormControlLabel
                            control={<Checkbox
                                checked={isPublicForm}
                                onChange={(event) => setIsPublicForm(event.target.checked ? 1 : 0)}
                            />}
                            label="Community Form"
                        />
                    </FormGroup>
                    <Button variant='contained' fullWidth onClick={() => addFormCopy()}>
                        Copy Form
                    </Button>
                </DialogContent>
            </Dialog>

            <Dialog open={openEditDialog} onClose={() => { setOpenEditDialog(false); }}>
                <DialogTitle>Confirm Action</DialogTitle>
                <DialogContent>
                    <div>This template has existing submissions. Editing it may cause previously submitted data to display incorrectly. Are you sure you want to continue?</div>
                    <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px', marginTop: '16px' }}>
                        <Button onClick={() => { setOpenEditDialog(false); }}>Cancel</Button>
                        <Button onClick={() => { setIsEdit(true); setOpenEditDialog(false); }} variant="contained" color="primary">Yes, Continue</Button>
                    </div>
                </DialogContent>
            </Dialog>

            <Dialog open={openDeleteDialog} onClose={handleClose}>
                <DialogTitle>Confirm Action</DialogTitle>
                <DialogContent>
                    <div>Once deleted, the form and any associated data will be permanently lost. Are you sure you want to continue?</div>
                    <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px', marginTop: '16px' }}>
                        <Button onClick={handleClose}>Cancel</Button>
                        <Button onClick={deleteForm} variant="contained" color="primary">Yes, Continue</Button>
                    </div>
                </DialogContent>
            </Dialog>
        </div>
    )
}